import * as React from 'react';
import BPromise from 'bluebird';
import propz from 'propz';
import moment from 'moment';
import { History, Location } from 'history';
import { AppUser } from '../../../../views/App/App';
import { Loader } from '../../../../components/Loader/Loader';
import { ListEmptyState } from '../../../../components/ListEmptyState/ListEmptyState';
import { faClipboardList } from '@fortawesome/free-solid-svg-icons';
import { SelectAllCheckbox } from 'Src/components/SelectAllCheckbox/SelectAllCheckbox';
import { HelpModal } from '../../../../components/HelpModal/HelpModal';
import { registerPageHelp } from '../../../../helpers/pageHelp/pageHelp';
import { DatePicker as SitDatePicker } from 'Src/components/DatePicker/DatePicker';
import type { DateRange } from 'Src/components/DatePicker/DatePicker';
import { FIRST_PAGE, FILTER_TYPE, DATE_INTERVAL, LIMIT } from 'Src/consts/table';
import {
  getClubProgrammeBookingClosesAt,
  getClubProgrammeBookingOpensAt,
  getClubProgrammePreBookingAccess,
  getClubProgrammeStatus
} from '../../../../helpers/accessor/accessor';
import {
  getOrder,
  getSearchFilter,
  getSearchOrder,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from '../../../../helpers/table/table';
import {
  getClubProgrammes,
  getAllClubProgrammes,
  getClubProgrammesCount,
  createClubProgramme,
  updateClubProgramme,
  batchActivateClubProgrammes,
  deleteClubProgramme
} from '../../../../helpers/service/admin/clubProgrammes';
import { clubProgrammeNormalizeCount } from '../../../../helpers/clubProgramme/clubProgramme';
import { getClubs } from '../../../../helpers/service/admin/clubs';
import { SchoolForm } from '../../../../models/form';
import { AfterSchoolClubProgramme } from '../../../../models/clubProgramme';
import {
  ClubProgrammeStatus,
  ClubProgrammeSelectionMode,
  ClubProgrammePreBookingAccess
} from '../../../../types/clubProgramme';
import { ClubProgrammeActivationWarningModal } from './components/ClubProgrammeActivationWarningModal';
import { ClubProgrammeDeleteModal } from './components/ClubProgrammeDeleteModal';
import { ClubProgrammeErrorModal } from './components/ClubProgrammeErrorModal';
import { ClubProgrammeStatusModal } from './components/ClubProgrammeStatusModal';
import { ClubProgrammesSummaryPanel } from './components/ClubProgrammeSummaryPanel';
import { buildClubProgrammesFilterOptions } from './helpers/clubProgrammeGrid';
import { CLUB_PROGRAMMES_GRID_COLUMNS } from './config/clubProgrammeGridColumns';
import { buildClubProgrammesQueryState } from './helpers/clubProgrammeQuery';
import './ClubProgrammes.css';

interface State {
  items: AfterSchoolClubProgramme[];
  selectedItems: AfterSchoolClubProgramme[];
  previewItem?: AfterSchoolClubProgramme;

  currentPage: number;
  totalWithFilters: number;
  total: number;

  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;

  isShowFilter: boolean;
  isColumnsOpen: boolean;
  isDataFiltered: boolean;
  filters: any;
  visibleColumns: { [field: string]: boolean };

  isSelectAllChecked: boolean;
  isLoading: boolean;
  basePath: string;

  isDeleteModalOpen: boolean;
  isCreateEditModalOpen: boolean;
  isHelpOpen: boolean;

  isEditMode: boolean;
  formName: string;
  formDescription: string;
  formStatus: ClubProgrammeStatus;
  formSelectionMode: ClubProgrammeSelectionMode;
  formBookingOpensAt: Date | null;
  formBookingClosesAt: Date | null;
  formAudienceAges: number[];
  formAudienceGenders: Array<'MALE' | 'FEMALE'>;
  formAudienceInvitations: 'PARENT' | 'STUDENT' | 'BOTH';
  formPriorityMax: number;

  isErrorModalOpen: boolean;
  errorMessage: string;

  currentTabIndex: number;
  forms: SchoolForm[];

  formPreBookingAccess: ClubProgrammePreBookingAccess;

  formDefaultFields: any[];

  isActivationWarningModalOpen: boolean;
  activationWarningItems: Array<{ programmeName: string; clubName: string }>;

  clubNamesById: { [clubId: string]: string };
  clubDetailsById: { [clubId: string]: any };

  backendOpensError: string;
  backendClosesError: string;
  backendFormError: string;

  formAllowChangesAfterSubmit: boolean;

  isStatusModalOpen: boolean;
  statusModalType: 'success' | 'info' | 'warning';
  statusModalTitle: string;
  statusModalMessage: string;
  statusModalLines: string[];
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

const getDefaultVisibleProgrammeColumns = (): { [field: string]: boolean } =>
  CLUB_PROGRAMMES_GRID_COLUMNS.reduce<{ [field: string]: boolean }>(
    (result, column) => ({
      ...result,
      [column.field]: true
    }),
    {}
  );

const getProgrammeColumnsStorageKey = (user: AppUser): string =>
  `columnVisibility__${propz.get(user, ['userId'], 'anonymous')}__club-programmes`;

const loadVisibleProgrammeColumns = (user: AppUser): { [field: string]: boolean } => {
  const defaults = getDefaultVisibleProgrammeColumns();

  try {
    const saved = window.localStorage.getItem(getProgrammeColumnsStorageKey(user));
    return saved ? { ...defaults, ...JSON.parse(saved), name: true } : defaults;
  } catch (error) {
    return defaults;
  }
};

export class ClubProgrammes extends React.Component<Props, State> {
  private unregisterPageHelp: (() => void) | null = null;

  constructor(props) {
    super(props);

    this.state = {
      items: [],
      selectedItems: [],
      previewItem: undefined,

      currentPage: FIRST_PAGE,
      totalWithFilters: 0,
      total: 0,

      sortDirection: '',
      sortColumnsName: '',

      isShowFilter: false,
      isColumnsOpen: false,
      isDataFiltered: false,
      filters: {},
      visibleColumns: loadVisibleProgrammeColumns(props.user),

      isSelectAllChecked: false,
      isLoading: false,
      basePath: '',

      isDeleteModalOpen: false,
      isCreateEditModalOpen: false,
      isHelpOpen: false,

      isEditMode: false,
      formName: '',
      formDescription: '',
      formStatus: 'DRAFT',
      formSelectionMode: 'SINGLE',
      formBookingOpensAt: null,
      formBookingClosesAt: null,
      formAudienceAges: [],
      formAudienceGenders: ['MALE', 'FEMALE'],
      formAudienceInvitations: 'PARENT',
      formPriorityMax: 10,

      isErrorModalOpen: false,
      errorMessage: '',

      currentTabIndex: 0,
      forms: [],

      formPreBookingAccess: 'VIEW',

      formDefaultFields: [],

      isActivationWarningModalOpen: false,
      activationWarningItems: [],

      clubNamesById: {},
      clubDetailsById: {},

      backendOpensError: '',
      backendClosesError: '',
      backendFormError: '',

      formAllowChangesAfterSubmit: false,

      isStatusModalOpen: false,
      statusModalType: 'success',
      statusModalTitle: '',
      statusModalMessage: '',
      statusModalLines: []
    };
  }

  componentDidMount() {
    document.addEventListener('keydown', this.onFilterPanelKeyDown);
    window.dispatchEvent(new CustomEvent('sit:topbar-breadcrumb', { detail: 'Clubs / Activity programmes' }));
    this.unregisterPageHelp = registerPageHelp(
      () => this.setState({ isHelpOpen: true }),
      'How activity programmes work'
    );
    this.setState({ isLoading: true }, () => this.setItems());
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.onFilterPanelKeyDown);
    window.dispatchEvent(new CustomEvent('sit:topbar-breadcrumb-clear'));
    if (this.unregisterPageHelp) this.unregisterPageHelp();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({ isLoading: true }, () => this.setItems());
    }
  }

  setItems = async () => {
    const { history, user } = this.props;

    const {
      page,
      filters,
      normalizedWhere,
      sortDirection,
      sortColumnsName,
      serverQueryFilter,
      isShowFilter,
      isDataFiltered
    } = buildClubProgrammesQueryState({
      searchString: history.location.search
    });

    try {
      const getItemsPromise = getClubProgrammes(user, serverQueryFilter);
      const getItemsCountPromise = getClubProgrammesCount(user, normalizedWhere);
      const getItemsTotalCountPromise = getClubProgrammesCount(user, {});

      const [totalWithFiltersObj, totalObj, items] = await BPromise.all([
        getItemsCountPromise,
        getItemsTotalCountPromise,
        getItemsPromise
      ]);

      const safeItems = Array.isArray(items) ? items : [];

      const params = new URLSearchParams(history.location.search || '');
      const selectedProgrammeId = String(params.get('selectedProgrammeId') || '');

      const restoredPreviewItem = selectedProgrammeId
        ? safeItems.find((item: any) => {
            const id = String(item?.id || item?._id || '');
            return id === selectedProgrammeId;
          })
        : undefined;

      await this.loadClubNamesForItems(safeItems);

      this.setState({
        currentPage: page,
        sortDirection,
        sortColumnsName,
        isShowFilter,
        isDataFiltered,
        filters,
        items: safeItems,
        selectedItems: [],
        previewItem: restoredPreviewItem,
        totalWithFilters: clubProgrammeNormalizeCount(totalWithFiltersObj),
        total: clubProgrammeNormalizeCount(totalObj),
        basePath: history.location.pathname,
        isLoading: false
      });
    } catch (err) {
      console.error('ClubProgrammes setItems error:', err);

      const errorText = propz.get(err, ['response', 'data', 'details', 'text'], 'Failed to load programmes.');

      this.setState({
        isLoading: false,
        isErrorModalOpen: true,
        errorMessage: errorText
      });
    }
  };

  setCurrentPageParams = (currentPage: number): void => {
    let search: string[] = [];

    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];

    const selectedItemIndex = selectedItems.findIndex(item => item.id === selectedItem.id);
    let selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      selectedItems: selectedItemsUpdated,
      currentTabIndex: 0
    });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;

    const itemsFiltered = items.filter(item => selectedItems.every(selectedItem => selectedItem.id !== item.id));
    const nextSelectedItems = [...selectedItems, ...itemsFiltered];

    this.setState({
      selectedItems: nextSelectedItems,
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = async (): Promise<void> => {
    const { user, history } = this.props;
    const { selectedItems } = this.state;

    this.setState({ isLoading: true });

    const { normalizedWhere } = buildClubProgrammesQueryState({
      searchString: history.location.search
    });

    try {
      const items = await getAllClubProgrammes(user, normalizedWhere);

      const itemsFiltered = items.filter(item => selectedItems.every(selectedItem => selectedItem.id !== item.id));

      const nextSelectedItems = [...selectedItems, ...itemsFiltered];

      this.setState({
        selectedItems: nextSelectedItems,
        isSelectAllChecked: true,
        isLoading: false
      });
    } catch (err) {
      console.error('onSelectAllClick error:', err);
      this.setState({ isLoading: false });
    }
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      previewItem: undefined,
      isSelectAllChecked: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;

    const order = getOrder(sortField, sortDirection, sortColumnsName);

    let search: string[] = [];
    search.push(`order=${sortField}:${order}`);

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (eventOrValue, filterField: string, options?): void => {
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];

    const rawValue = eventOrValue && eventOrValue.target ? eventOrValue.target.value : eventOrValue;

    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      const isFrom = options === DATE_INTERVAL.FROM || options === 'from';
      const isTo = options === DATE_INTERVAL.TO || options === 'to';

      let v: any = rawValue;
      if (v instanceof Date) {
        v = moment(v).format('YYYY-MM-DD');
      }

      if (isFrom) {
        nextFilters = {
          ...nextFilters,
          [filterField]: {
            ...(currentFilterField || {}),
            from: v
          }
        };
      }

      if (isTo) {
        nextFilters = {
          ...nextFilters,
          [filterField]: {
            ...(currentFilterField || {}),
            to: v
          }
        };
      }

      this.setState({ filters: nextFilters });
      return;
    }

    const col = CLUB_PROGRAMMES_GRID_COLUMNS.find(c => c.field === filterField);
    const filterType = col?.type;

    if (filterType === FILTER_TYPE.MULTISELECT) {
      const opts = eventOrValue?.target?.options;
      const value: string[] = [];

      if (opts && typeof opts.length === 'number') {
        for (let i = 0; i < opts.length; i++) {
          if (opts[i].selected) value.push(String(opts[i].value));
        }
      } else if (Array.isArray(rawValue)) {
        value.push(...rawValue.map(x => String(x)));
      }

      nextFilters = {
        ...nextFilters,
        [filterField]: value
      };

      this.setState({ filters: nextFilters });
      return;
    }

    nextFilters = {
      ...nextFilters,
      [filterField]: rawValue
    };

    this.setState({ filters: nextFilters });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    let search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      previewItem: undefined,
      isSelectAllChecked: false,
      isShowFilter: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onClearFilterClick = (): void => {
    this.setState({
      selectedItems: [],
      previewItem: undefined,
      isSelectAllChecked: false,
      isShowFilter: false,
      filters: {}
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: ''
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    this.setState({
      isShowFilter: !this.state.isShowFilter,
      isColumnsOpen: false
    });
  };

  toggleColumn = (field: string): void => {
    if (field === 'name') return;
    this.setState(
      prev => ({
        visibleColumns: {
          ...prev.visibleColumns,
          [field]: !prev.visibleColumns[field]
        }
      }),
      () => {
        try {
          window.localStorage.setItem(
            getProgrammeColumnsStorageKey(this.props.user),
            JSON.stringify(this.state.visibleColumns)
          );
        } catch (error) {
          // Column preferences are optional when storage is unavailable.
        }
      }
    );
  };

  resetColumns = (): void => {
    const visibleColumns = getDefaultVisibleProgrammeColumns();
    this.setState({ visibleColumns }, () => {
      try {
        window.localStorage.setItem(getProgrammeColumnsStorageKey(this.props.user), JSON.stringify(visibleColumns));
      } catch (error) {
        // Column preferences are optional when storage is unavailable.
      }
    });
  };

  getVisibleProgrammeColumns = () =>
    CLUB_PROGRAMMES_GRID_COLUMNS.filter(column => column.field === 'name' || this.state.visibleColumns[column.field]);

  onCancelFilterClick = (): void => {
    const { filters } = buildClubProgrammesQueryState({
      searchString: this.props.history.location.search
    });

    this.setState({
      filters,
      isShowFilter: false
    });
  };

  onFilterPanelKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.state.isShowFilter) {
      this.onCancelFilterClick();
    } else if (event.key === 'Escape' && this.state.isColumnsOpen) {
      this.setState({ isColumnsOpen: false });
    }
  };

  getAppliedFilters = (): any => {
    return buildClubProgrammesQueryState({
      searchString: this.props.history.location.search
    }).filters;
  };

  removeAppliedFilter = (field: string): void => {
    const nextFilters = { ...this.getAppliedFilters() };
    delete nextFilters[field];

    const search: string[] = [];
    if (isSortExist(this.state.sortDirection, this.state.sortColumnsName)) {
      search.push(getSearchOrder(this.state.sortDirection, this.state.sortColumnsName));
    }
    if (isFilterExist2(nextFilters)) {
      search.push(getSearchFilter(nextFilters));
    }

    this.setState({ filters: nextFilters, isShowFilter: false });
    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  getActiveFilterChips = (): Array<{ field: string; label: string }> => {
    const filters = this.getAppliedFilters();
    const options = buildClubProgrammesFilterOptions();
    const chips: Array<{ field: string; label: string }> = [];

    if (String(filters?.name || '').trim()) {
      chips.push({ field: 'name', label: `Search: ${String(filters.name).trim()}` });
    }

    (['bookingOpensAt', 'bookingClosesAt'] as string[]).forEach(field => {
      const value = filters?.[field] || {};
      if (!value.from && !value.to) return;
      const title = field === 'bookingOpensAt' ? 'Booking opens' : 'Booking closes';
      const formatDate = (date: string) => {
        const parsed = moment(String(date), ['YYYY-MM-DD', 'DD-MM-YYYY', 'DD/MM/YYYY'], true);
        return parsed.isValid() ? parsed.format('DD MMM YYYY') : date;
      };
      const range =
        value.from && value.to
          ? `${formatDate(value.from)} – ${formatDate(value.to)}`
          : value.from
          ? `from ${formatDate(value.from)}`
          : `to ${formatDate(value.to)}`;
      chips.push({ field, label: `${title}: ${range}` });
    });

    (['status', 'selectionMode', 'preBookingAccess'] as string[]).forEach(field => {
      const selected = Array.isArray(filters?.[field]) ? filters[field].map(String) : [];
      if (!selected.length) return;
      const title = CLUB_PROGRAMMES_GRID_COLUMNS.find(column => column.field === field)?.text || field;
      const labels = selected.map(value => {
        const option = (options[field] || []).find(item => String(item.value) === value);
        return String(option?.text || value);
      });
      chips.push({ field, label: `${title}: ${labels.join(', ')}` });
    });

    return chips;
  };

  onProgrammeUpdated = (updated: AfterSchoolClubProgramme) => {
    const updatedId = (updated as any).id || (updated as any)._id;

    this.setState(prev => {
      const items = (prev.items || []).map(p => {
        const pid = (p as any).id || (p as any)._id;
        return String(pid) === String(updatedId) ? updated : p;
      });

      const selectedItems = (prev.selectedItems || []).map(p => {
        const pid = (p as any).id || (p as any)._id;
        return String(pid) === String(updatedId) ? updated : p;
      });

      const previewItem = prev.previewItem && String((prev.previewItem as any).id || (prev.previewItem as any)._id || '') === String(updatedId) ? updated : prev.previewItem;

      return { items, selectedItems, previewItem };
    });
  };

  isOneItemSelected(): boolean {
    return this.state.selectedItems.length === 1;
  }

  loadClubNamesForItems = async (items: AfterSchoolClubProgramme[]) => {
    const { user } = this.props;

    const clubIdSet = new Set<string>();

    (items || []).forEach((programme: any) => {
      const clubs = Array.isArray(programme?.clubs) ? programme.clubs : [];
      clubs.forEach((pc: any) => {
        const clubId = String(pc?.clubId || '');
        if (clubId) clubIdSet.add(clubId);
      });
    });

    const clubIds = Array.from(clubIdSet);
    if (!clubIds.length) {
      this.setState({ clubNamesById: {}, clubDetailsById: {} });
      return;
    }

    try {
      const clubs = await getClubs(user, {
        limit: 5000,
        skip: 0,
        where: { _id: { $in: clubIds } },
        sort: 'name ASC'
      } as any);

      const clubNamesById = (Array.isArray(clubs) ? clubs : []).reduce((acc: any, club: any) => {
        const id = String(club?.id || club?._id || '');
        const name = String(club?.name || '');
        if (id) acc[id] = name || id;
        return acc;
      }, {});

      const clubDetailsById = (Array.isArray(clubs) ? clubs : []).reduce((acc: any, club: any) => {
        const id = String(club?.id || club?._id || '');
        if (id) acc[id] = club;
        return acc;
      }, {});

      this.setState({ clubNamesById, clubDetailsById });
    } catch (err) {
      console.error('loadClubNamesForItems error:', err);
      this.setState({ clubNamesById: {}, clubDetailsById: {} });
    }
  };

  isActivateAllowed(): boolean {
    const { selectedItems } = this.state;
    if (!selectedItems.length) return false;

    return selectedItems.every(p => {
      const status = propz.get(p, ['status'], 'DRAFT');
      const clubs = propz.get(p, ['clubs'], []);
      const enabledCount = Array.isArray(clubs) ? clubs.filter((c: any) => c && c.isEnabled !== false).length : 0;

      return status === 'DRAFT' && enabledCount > 0;
    });
  }

  getActivateDisabledReason(): string {
    const { selectedItems } = this.state;
    if (!selectedItems.length) return '';

    const hasNonDraft = selectedItems.some(p => {
      const status = propz.get(p, ['status'], 'DRAFT');
      return status !== 'DRAFT';
    });

    if (hasNonDraft) {
      return 'only draft programmes can be activated';
    }

    const hasNoClubs = selectedItems.some(p => {
      const clubs = propz.get(p, ['clubs'], []);
      return !Array.isArray(clubs) || clubs.length === 0;
    });

    if (hasNoClubs) {
      return 'add at least one club first';
    }

    const hasNoEnabledClubs = selectedItems.some(p => {
      const clubs = propz.get(p, ['clubs'], []);
      const enabledCount = Array.isArray(clubs) ? clubs.filter((c: any) => c && c.isEnabled !== false).length : 0;

      return enabledCount === 0;
    });

    if (hasNoEnabledClubs) {
      return 'enable at least one club first';
    }

    return '';
  }

  onActivateProgrammesClick = () => {
    const { selectedItems } = this.state;

    if (!selectedItems.length) return;

    const warningItems = this.getActivationInvitesOnlyWarnings();

    if (warningItems.length > 0) {
      this.setState({
        isActivationWarningModalOpen: true,
        activationWarningItems: warningItems
      });
      return;
    }

    this.runActivateProgrammes();
  };

  getActivationInvitesOnlyWarnings(): Array<{ programmeName: string; clubName: string }> {
    const { selectedItems, clubNamesById } = this.state;

    const out: Array<{ programmeName: string; clubName: string }> = [];

    (selectedItems || []).forEach((programme: any) => {
      const programmeName = String(programme?.name || 'Programme');
      const clubs = Array.isArray(programme?.clubs) ? programme.clubs : [];

      clubs.forEach((pc: any) => {
        if (!pc) return;
        if (pc.isEnabled === false) return;
        if (!pc.invitesOnly) return;

        const allowList = Array.isArray(pc.allowList) ? pc.allowList : [];
        if (allowList.length > 0) return;

        const clubId = String(pc?.clubId || '');
        const clubName = String(clubNamesById?.[clubId] || pc?.clubName || pc?.name || clubId || 'Club');

        out.push({ programmeName, clubName });
      });
    });

    return out;
  }

  buildActivationStatusModalState(result: {
    activated: string[];
    skipped: Array<{ programmeId: string; reason: string }>;
  }) {
    const activatedCount = Array.isArray(result?.activated) ? result.activated.length : 0;
    const skippedCount = Array.isArray(result?.skipped) ? result.skipped.length : 0;

    const title = activatedCount === 1 ? 'Programme activated successfully' : 'Programmes activated successfully';

    const message =
      activatedCount === 1
        ? 'The selected programme is now active and invitations have been prepared for eligible users.'
        : `${activatedCount} programmes are now active and invitations have been prepared for eligible users.`;

    const lines: string[] = [];

    if (activatedCount > 0) {
      lines.push(activatedCount === 1 ? '1 programme was activated.' : `${activatedCount} programmes were activated.`);
    }

    if (skippedCount > 0) {
      lines.push(
        skippedCount === 1
          ? '1 programme was skipped because it was not eligible for activation.'
          : `${skippedCount} programmes were skipped because they were not eligible for activation.`
      );
    }

    lines.push(
      'The programme is now live. Parent and student visibility now follows the selected pre-booking access mode.'
    );

    return {
      isStatusModalOpen: true,
      statusModalType: 'success' as const,
      statusModalTitle: title,
      statusModalMessage: message,
      statusModalLines: lines
    };
  }

  runActivateProgrammes = () => {
    const { user } = this.props;
    const { selectedItems } = this.state;

    if (!selectedItems.length) return;

    const programmeIdList = selectedItems.map((p: any) => String(p.id || p._id || '')).filter(Boolean);

    if (!programmeIdList.length) {
      this.setState({
        isErrorModalOpen: true,
        errorMessage: 'No programme ids selected.'
      });
      return;
    }

    this.setState({ isLoading: true });

    batchActivateClubProgrammes(user, programmeIdList)
      .then(result => {
        this.setState(
          {
            isLoading: false,
            isSelectAllChecked: false,
            selectedItems: [],
      previewItem: undefined,
            isActivationWarningModalOpen: false,
            activationWarningItems: [],
            ...this.buildActivationStatusModalState(result)
          },
          () => {
            this.setItems();
          }
        );
      })
      .catch(err => {
        console.error('batchActivateClubProgrammes error:', err);
        const errorText = propz.get(err, ['response', 'data', 'details', 'text'], 'Failed to activate programme(s).');
        this.setState({
          isLoading: false,
          isErrorModalOpen: true,
          errorMessage: errorText,
          isActivationWarningModalOpen: false
        });
      });
  };

  isDeleteAllowed(): boolean {
    const { selectedItems } = this.state;

    if (selectedItems.length === 0) return false;

    return selectedItems.every(p => {
      const status = propz.get(p, ['status'], 'DRAFT');
      const clubs = propz.get(p, ['clubs'], []);
      const clubsCount = Array.isArray(clubs) ? clubs.length : 0;

      return status === 'DRAFT' && clubsCount === 0;
    });
  }

  openCreateModal = () => {
    this.props.history.push({
      pathname: '/clubProgrammes/create',
      state: {
        from: {
          pathname: this.props.location.pathname,
          search: this.props.location.search
        }
      }
    });
  };

  openEditModal = () => {
    if (this.state.selectedItems.length !== 1) return;
    const p = this.state.selectedItems[0];
    const programmeId = String((p as any)?.id || (p as any)?._id || '');
    if (!programmeId) return;

    this.props.history.push({
      pathname: `/clubProgrammes/${programmeId}/edit`,
      state: {
        from: {
          pathname: this.props.location.pathname,
          search: this.props.location.search
        }
      }
    });
  };

  closeCreateEditModal = () => {
    this.setState({
      isCreateEditModalOpen: false,
      backendOpensError: '',
      backendClosesError: '',
      backendFormError: ''
    });
  };

  onSaveProgrammeClick = () => {
    const { user } = this.props;
    const {
      isEditMode,
      selectedItems,
      formName,
      formDescription,
      formSelectionMode,
      formBookingOpensAt,
      formBookingClosesAt,
      formAudienceAges,
      formAudienceGenders,
      formAudienceInvitations,
      formPriorityMax,
      formPreBookingAccess,
      formAllowChangesAfterSubmit
    } = this.state;

    this.setState({
      backendOpensError: '',
      backendClosesError: '',
      backendFormError: ''
    });

    const name = (formName || '').trim();

    if (!name || !formBookingOpensAt || !formBookingClosesAt) {
      this.setState({
        isErrorModalOpen: true,
        errorMessage: 'Please fill Name, Booking opens and Booking closes.'
      });
      return;
    }

    if (formBookingOpensAt.getTime() >= formBookingClosesAt.getTime()) {
      this.setState({
        isErrorModalOpen: true,
        errorMessage: 'Booking closes must be after Booking opens.'
      });
      return;
    }

    if (!Array.isArray(formAudienceAges) || formAudienceAges.length === 0) {
      this.setState({
        isErrorModalOpen: true,
        errorMessage: 'Please select at least one audience age group.'
      });
      return;
    }

    if (!Array.isArray(formAudienceGenders) || formAudienceGenders.length === 0) {
      this.setState({
        isErrorModalOpen: true,
        errorMessage: 'Please select at least one audience gender.'
      });
      return;
    }

    if (!Number.isFinite(formPriorityMax) || formPriorityMax < 2 || formPriorityMax > 10) {
      this.setState({
        isErrorModalOpen: true,
        errorMessage: 'Priority limit must be between 2 and 10.'
      });
      return;
    }

    const description = (formDescription || '').trim();

    const createPayload: any = {
      name,
      description,
      booking: {
        opensAt: formBookingOpensAt.toISOString(),
        closesAt: formBookingClosesAt.toISOString(),
        preBookingAccess: formPreBookingAccess,
        allowChangesAfterSubmit: formAllowChangesAfterSubmit
      },
      selectionRules: {
        mode: formSelectionMode,
        priorityRange: {
          min: 1,
          max: Math.trunc(formPriorityMax)
        }
      },
      audience: {
        ages: Array.isArray(formAudienceAges) ? formAudienceAges : [],
        genders: Array.isArray(formAudienceGenders) ? formAudienceGenders : [],
        invitations: formAudienceInvitations
      }
    };

    this.setState({ isLoading: true });

    let promise;

    if (isEditMode) {
      const selectedProgramme = selectedItems[0] as any;
      const programmeId = String(selectedProgramme?.id || selectedProgramme?._id || '');

      const initialName = String(propz.get(selectedProgramme, ['name'], '') || '').trim();
      const initialDescription = String(propz.get(selectedProgramme, ['description'], '') || '').trim();

      const initialPreBookingAccess = String(
        propz.get(selectedProgramme, ['booking', 'preBookingAccess'], 'VIEW') || 'VIEW'
      ).toUpperCase();

      const initialAllowChangesAfterSubmit =
        typeof propz.get(selectedProgramme, ['booking', 'allowChangesAfterSubmit']) === 'boolean'
          ? Boolean(propz.get(selectedProgramme, ['booking', 'allowChangesAfterSubmit']))
          : false;

      const initialOpensAtRaw = propz.get(selectedProgramme, ['booking', 'opensAt'], null);
      const initialClosesAtRaw = propz.get(selectedProgramme, ['booking', 'closesAt'], null);

      const initialOpensAt = initialOpensAtRaw ? new Date(initialOpensAtRaw) : null;
      const initialClosesAt = initialClosesAtRaw ? new Date(initialClosesAtRaw) : null;

      const initialSelectionMode = String(
        propz.get(selectedProgramme, ['selectionRules', 'mode'], 'SINGLE') || 'SINGLE'
      ).toUpperCase();

      const initialPriorityMax = Number(propz.get(selectedProgramme, ['selectionRules', 'priorityRange', 'max'], 10));

      const initialAudienceAges = Array.isArray(propz.get(selectedProgramme, ['audience', 'ages'], []))
        ? propz
            .get(selectedProgramme, ['audience', 'ages'], [])
            .map((x: any) => Number(x))
            .filter((x: number) => Number.isFinite(x))
            .map((x: number) => Math.trunc(x))
        : [];

      const initialAudienceGenders = Array.isArray(propz.get(selectedProgramme, ['audience', 'genders'], []))
        ? propz.get(selectedProgramme, ['audience', 'genders'], []).filter((x: any) => x === 'MALE' || x === 'FEMALE')
        : [];

      const initialAudienceInvitations = String(
        propz.get(selectedProgramme, ['audience', 'invitations'], 'PARENT') || 'PARENT'
      ).toUpperCase();

      const patch: any = {};
      const bookingPatch: any = {};

      const sameDateTime = (a?: Date | null, b?: Date | null) => {
        if (!a && !b) return true;
        if (!a || !b) return false;
        return a.getTime() === b.getTime();
      };

      const sameNumberArray = (a: number[], b: number[]) => {
        if (a.length !== b.length) return false;
        return a.every((x, idx) => x === b[idx]);
      };

      const sameStringArray = (a: string[], b: string[]) => {
        if (a.length !== b.length) return false;
        return a.every((x, idx) => x === b[idx]);
      };

      if (name !== initialName) {
        patch.name = name;
      }

      if (description !== initialDescription) {
        patch.description = description;
      }

      if (!sameDateTime(formBookingOpensAt, initialOpensAt)) {
        bookingPatch.opensAt = formBookingOpensAt.toISOString();
      }

      if (!sameDateTime(formBookingClosesAt, initialClosesAt)) {
        bookingPatch.closesAt = formBookingClosesAt.toISOString();
      }

      if (String(formPreBookingAccess || '').toUpperCase() !== initialPreBookingAccess) {
        bookingPatch.preBookingAccess = formPreBookingAccess;
      }

      if (Boolean(formAllowChangesAfterSubmit) !== initialAllowChangesAfterSubmit) {
        bookingPatch.allowChangesAfterSubmit = Boolean(formAllowChangesAfterSubmit);
      }

      if (Object.keys(bookingPatch).length > 0) {
        patch.booking = bookingPatch;
      }

      if (
        String(formSelectionMode || '').toUpperCase() !== initialSelectionMode ||
        Math.trunc(Number(formPriorityMax)) !== Math.trunc(Number(initialPriorityMax))
      ) {
        patch.selectionRules = {
          mode: formSelectionMode,
          priorityRange: {
            min: 1,
            max: Math.trunc(formPriorityMax)
          }
        };
      }

      const nextAudienceAges = (Array.isArray(formAudienceAges) ? formAudienceAges : [])
        .map(x => Math.trunc(Number(x)))
        .filter(x => Number.isFinite(x));

      const nextAudienceGenders = Array.isArray(formAudienceGenders) ? formAudienceGenders : [];

      if (
        !sameNumberArray(nextAudienceAges, initialAudienceAges) ||
        !sameStringArray(nextAudienceGenders, initialAudienceGenders) ||
        String(formAudienceInvitations || '').toUpperCase() !== initialAudienceInvitations
      ) {
        patch.audience = {
          ages: nextAudienceAges,
          genders: nextAudienceGenders,
          invitations: formAudienceInvitations
        };
      }

      promise = updateClubProgramme(user, patch, programmeId);
    } else {
      promise = createClubProgramme(user, createPayload);
    }

    promise
      .then((savedProgramme: any) => {
        const savedProgrammeId = String(savedProgramme?.id || savedProgramme?._id || '');

        this.setState(
          {
            isCreateEditModalOpen: false,
            isLoading: false,
            isSelectAllChecked: false,
            selectedItems: [],
      previewItem: undefined,
            currentTabIndex: 0,
            backendOpensError: '',
            backendClosesError: '',
            backendFormError: ''
          },
          () => {
            if (!isEditMode && savedProgrammeId) {
              this.props.history.push({
                pathname: this.state.basePath || this.props.location.pathname,
                search: this.getBackSearch(savedProgrammeId)
              });
            } else {
              this.setItems();
            }
          }
        );
      })
      .catch(err => {
        console.error('save programme error:', err);

        const errorText =
          propz.get(err, ['response', 'data', 'error']) ||
          propz.get(err, ['response', 'data', 'details', 'text']) ||
          propz.get(err, ['message']) ||
          'Failed to save programme.';

        const opensLocked = errorText === 'booking.opensAt cannot be changed after booking has opened';
        const closesLocked = errorText === 'booking.closesAt cannot be changed after booking has closed';

        this.setState({
          isLoading: false,
          isErrorModalOpen: false,
          errorMessage: '',
          backendOpensError: opensLocked
            ? 'Booking start can no longer be changed because booking has already opened.'
            : '',
          backendClosesError: closesLocked
            ? 'Booking end can no longer be changed because booking has already closed.'
            : '',
          backendFormError: !opensLocked && !closesLocked ? errorText : ''
        });
      });
  };

  openDeleteModal = () => this.setState({ isDeleteModalOpen: true });
  closeDeleteModal = () => this.setState({ isDeleteModalOpen: false });

  onDeleteProgrammeClick = () => {
    const { user } = this.props;
    const { selectedItems } = this.state;

    this.setState({ isLoading: true });

    BPromise.all(selectedItems.map((p: any) => deleteClubProgramme(user, String(p.id || p._id || ''))))
      .then(() => {
        this.setState({
          isDeleteModalOpen: false,
          isLoading: false,
          isSelectAllChecked: false,
          selectedItems: []
        });
        this.setItems();
      })
      .catch(err => {
        console.error('delete programme error:', err);
        const errorText = propz.get(err, ['response', 'data', 'details', 'text'], 'Failed to delete programme(s).');
        this.setState({ isLoading: false, isErrorModalOpen: true, errorMessage: errorText });
      });
  };

  getBackLocation = (selectedProgrammeId?: string) => ({
    pathname: this.props.location.pathname,
    search: this.props.location.search,
    state: {
      selectedProgrammeId: selectedProgrammeId || ''
    }
  });

  getBackSearch = (selectedProgrammeId?: string): string => {
    const params = new URLSearchParams(this.props.location.search || '');

    if (selectedProgrammeId) {
      params.set('selectedProgrammeId', selectedProgrammeId);
    } else {
      params.delete('selectedProgrammeId');
    }

    const s = params.toString();
    return s ? `?${s}` : '';
  };

  onTabClick = (event, index: number) => {
    event.preventDefault();
    this.setState({ currentTabIndex: index });
  };

  onProgrammePreviewClick = (programme: AfterSchoolClubProgramme): void => {
    this.setState({
      previewItem: programme,
      currentTabIndex: 0
    });
  };

  closeProgrammePreview = (): void => {
    this.setState({ previewItem: undefined });
  };

  getProgrammeId = (programme: AfterSchoolClubProgramme): string => {
    return String((programme as any)?.id || (programme as any)?._id || '');
  };

  isProgrammeSelected = (programme: AfterSchoolClubProgramme): boolean => {
    const programmeId = this.getProgrammeId(programme);
    return this.state.selectedItems.some(item => this.getProgrammeId(item) === programmeId);
  };

  getDsDateFilterDate = (value: any): Date | null => {
    if (!value) return null;

    if (value instanceof Date) return value;

    const parsed = moment(String(value), ['YYYY-MM-DD', 'DD-MM-YYYY', 'DD/MM/YYYY'], true);
    return parsed.isValid() ? parsed.toDate() : null;
  };

  getDsDateFilterRange = (field: string): DateRange => {
    const filter = this.state.filters?.[field] || {};

    return {
      start: this.getDsDateFilterDate(filter.from),
      end: this.getDsDateFilterDate(filter.to)
    };
  };

  setDsDateRangeFilterValue = (field: string, value: DateRange | Date | null): void => {
    const nextValue = value && !(value instanceof Date) ? value : { start: null, end: null };

    this.setState(prev => ({
      filters: {
        ...prev.filters,
        [field]: {
          from: nextValue.start ? moment(nextValue.start).format('YYYY-MM-DD') : '',
          to: nextValue.end ? moment(nextValue.end).format('YYYY-MM-DD') : ''
        }
      }
    }));
  };

  onDsToggleMultiselectFilter = (field: string, value: string): void => {
    const currentValue = this.state.filters?.[field];
    const current = Array.isArray(currentValue) ? currentValue.map(String) : [];
    const nextValue = current.includes(value) ? current.filter(item => item !== value) : [...current, value];

    this.setState(prev => ({
      filters: {
        ...prev.filters,
        [field]: nextValue
      }
    }));
  };

  getProgrammeStatusClass = (status: string): string => {
    switch (String(status || '').toUpperCase()) {
      case 'ACTIVE':
        return 'isSuccess';
      case 'COMPLETED':
        return 'isInfo';
      case 'ARCHIVED':
        return 'isNeutral';
      default:
        return 'isWarning';
    }
  };

  renderProgrammeStatus = (programme: AfterSchoolClubProgramme): React.ReactNode => {
    const status = getClubProgrammeStatus(programme) || 'DRAFT';
    return <span className={`cpDsBadge ${this.getProgrammeStatusClass(status)}`}>{status}</span>;
  };

  renderProgrammeNameCell = (programme: AfterSchoolClubProgramme): React.ReactNode => {
    const description = String((programme as any)?.description || '').trim();

    return (
      <div className="cpDsNameCell">
        <span className="cpDsProgrammeIcon">CP</span>
        <div>
          <strong>{String((programme as any)?.name || 'Untitled programme')}</strong>
          {description ? <small>{description}</small> : <small>No description</small>}
        </div>
      </div>
    );
  };

  renderProgrammeCell = (programme: AfterSchoolClubProgramme, field: string): React.ReactNode => {
    switch (field) {
      case 'name':
        return this.renderProgrammeNameCell(programme);
      case 'bookingOpensAt':
        return getClubProgrammeBookingOpensAt(programme) || '-';
      case 'bookingClosesAt':
        return getClubProgrammeBookingClosesAt(programme) || '-';
      case 'preBookingAccess':
        return <span className="cpDsChip">{getClubProgrammePreBookingAccess(programme)}</span>;
      case 'selectionMode':
        return <span className="cpDsChip">{propz.get(programme, ['selectionRules', 'mode'], '') || '-'}</span>;
      case 'status':
        return this.renderProgrammeStatus(programme);
      case 'clubsCount':
        return <strong>{Array.isArray((programme as any)?.clubs) ? (programme as any).clubs.length : 0}</strong>;
      default:
        return '';
    }
  };

  renderDsFilters = (): React.ReactNode => {
    const filterOptions = buildClubProgrammesFilterOptions();

    return (
      <div className="cpDsFilterFields">
        <div className="cpDsFilterField cpDsFilterFieldWide">
          <SitDatePicker
            className="cpDsDateRange"
            label="Booking opens"
            mode="range"
            value={this.getDsDateFilterRange('bookingOpensAt')}
            onChange={nextValue => this.setDsDateRangeFilterValue('bookingOpensAt', nextValue)}
            format="DD-MM-YYYY"
            placeholder="Select date range"
            numberOfMonths={2}
            align="left"
            clearable={true}
          />
        </div>

        <div className="cpDsFilterField cpDsFilterFieldWide">
          <SitDatePicker
            className="cpDsDateRange"
            label="Booking closes"
            mode="range"
            value={this.getDsDateFilterRange('bookingClosesAt')}
            onChange={nextValue => this.setDsDateRangeFilterValue('bookingClosesAt', nextValue)}
            format="DD-MM-YYYY"
            placeholder="Select date range"
            numberOfMonths={2}
            align="left"
            clearable={true}
          />
        </div>

        {(['status', 'selectionMode', 'preBookingAccess'] as string[]).map(field => {
          const selected = Array.isArray(this.state.filters?.[field]) ? this.state.filters[field].map(String) : [];
          const column = CLUB_PROGRAMMES_GRID_COLUMNS.find(item => item.field === field);
          const options = filterOptions[field] || [];

          return (
            <div key={field} className="cpDsFilterField cpDsFilterFieldWide">
              <span>{column?.text || field}</span>
              <div className="cpDsFilterChips" role="group" aria-label={column?.text || field}>
                {options.map(option => {
                  const value = String(option.value);
                  const isSelected = selected.includes(value);

                  return (
                    <button
                      key={value}
                      type="button"
                      className={`cpDsFilterChip ${isSelected ? 'isSelected' : ''}`}
                      onClick={() => this.onDsToggleMultiselectFilter(field, value)}
                    >
                      {String(option.text)}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  renderDsSelectionBar = (): React.ReactNode => {
    const selectedCount = this.state.selectedItems.length;
    if (!selectedCount) return null;

    const canEdit = selectedCount === 1 && getClubProgrammeStatus(this.state.selectedItems[0]) !== 'ARCHIVED';
    const canActivate = this.isActivateAllowed();
    const canDelete = this.isDeleteAllowed();

    return (
      <div className="cpDsSelectionBar">
        <div className="cpDsSelectionCount">{selectedCount} selected</div>
        <button type="button" className="cpDsBtn cpDsBtnSecondary" disabled={!canEdit} onClick={this.openEditModal}>Edit</button>
        <button
          type="button"
          className="cpDsBtn cpDsBtnSecondary"
          disabled={!canActivate}
          onClick={this.onActivateProgrammesClick}
          title={canActivate ? '' : this.getActivateDisabledReason()}
        >
          Activate
        </button>
        <button type="button" className="cpDsBtn cpDsBtnDanger" disabled={!canDelete} onClick={this.openDeleteModal}>Delete</button>
        <button type="button" className="cpDsBtn cpDsBtnGhost" onClick={this.onUnselectAllClick}>Clear</button>
      </div>
    );
  };

  renderDsTable = (): React.ReactNode => {
    const allPageSelected = this.state.items.length > 0 && this.state.items.every(item => this.isProgrammeSelected(item));
    const visibleColumns = this.getVisibleProgrammeColumns();

    return (
      <div className="cpDsTableCard">
        <div className="cpDsTableScroll">
          <table className="cpDsTable">
            <thead>
              <tr>
                <th className="cpDsCheckCell">
                  <SelectAllCheckbox
                    compact
                    isChecked={allPageSelected || this.state.isSelectAllChecked}
                    totalCount={this.state.totalWithFilters}
                    currentPage={this.state.currentPage}
                    pageItemsCount={this.state.items.length}
                    selectedItems={this.state.selectedItems}
                    onSelectAllOnPageClick={this.onSelectAllOnPageClick}
                    onSelectAllClick={this.onSelectAllClick}
                    onUnselectAllClick={this.onUnselectAllClick}
                  />
                </th>
                {visibleColumns.map(column => (
                  <th key={column.field}>
                    {column.isSort ? (
                      <button type="button" className="cpDsSort" onClick={() => this.onTableSortClick(column.field)}>
                        {column.text}
                        {this.state.sortColumnsName === column.field && this.state.sortDirection ? (
                          <span className="cpDsSortMark">{this.state.sortDirection}</span>
                        ) : null}
                      </button>
                    ) : (
                      column.text
                    )}
                  </th>
                ))}
                <th className="cpDsOpenCell" aria-label="Open programme" />
              </tr>
            </thead>
            <tbody>
              {this.state.items.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length + 2} className="cpDsEmpty">
                    <ListEmptyState
                      icon={faClipboardList}
                      title={
                        this.state.isDataFiltered ? 'No activity programmes match your filters' : 'No activity programmes yet'
                      }
                      description={
                        this.state.isDataFiltered
                          ? 'Try changing or clearing the search and filters to see other programmes.'
                          : 'Create a programme to organise club booking windows, audiences and activities.'
                      }
                    />
                  </td>
                </tr>
              ) : (
                this.state.items.map((programme, index) => {
                  const isSelected = this.isProgrammeSelected(programme);

                  return (
                    <tr
                      key={this.getProgrammeId(programme) || index}
                      className={`${isSelected ? 'isSelected' : ''} ${this.state.previewItem && this.getProgrammeId(this.state.previewItem) === this.getProgrammeId(programme) ? 'isPreview' : ''}`.trim()}
                      onClick={() => this.onProgrammePreviewClick(programme)}
                    >
                      <td className="cpDsCheckCell" onClick={event => event.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => this.onItemClick(index)}
                          aria-label="Select programme"
                        />
                      </td>
                      {visibleColumns.map(column => (
                        <td key={column.field}>{this.renderProgrammeCell(programme, column.field)}</td>
                      ))}
                      <td className="cpDsOpenCell">
                        <span aria-hidden="true">›</span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  renderDsPagination = (): React.ReactNode => {
    const total = Number(this.state.totalWithFilters || 0);
    const currentPage = Number(this.state.currentPage || FIRST_PAGE);
    const pageCount = Math.max(1, Math.ceil(total / LIMIT));
    const pageWindowStart = Math.max(1, currentPage - 2);
    const pageWindowEnd = Math.min(pageCount, currentPage + 2);
    const pages: number[] = [];

    for (let page = pageWindowStart; page <= pageWindowEnd; page++) {
      pages.push(page);
    }

    return (
      <div className="cpDsFooter">
        <span className="cpDsPaginationSummary">Page {currentPage} of {pageCount}</span>
        <button type="button" className="cpDsPagerBtn" disabled={currentPage <= 1} onClick={() => this.setCurrentPageParams(currentPage - 1)}>Previous</button>
        {pageWindowStart > 1 ? (
          <>
            <button type="button" className="cpDsPageBtn" onClick={() => this.setCurrentPageParams(1)}>1</button>
            {pageWindowStart > 2 ? <span className="cpDsPaginationEllipsis">...</span> : null}
          </>
        ) : null}
        {pages.map(pageNumber => (
          <button
            key={pageNumber}
            type="button"
            className={`cpDsPageBtn ${pageNumber === currentPage ? 'isActive' : ''}`}
            onClick={() => this.setCurrentPageParams(pageNumber)}
          >
            {pageNumber}
          </button>
        ))}
        {pageWindowEnd < pageCount ? (
          <>
            {pageWindowEnd < pageCount - 1 ? <span className="cpDsPaginationEllipsis">...</span> : null}
            <button type="button" className="cpDsPageBtn" onClick={() => this.setCurrentPageParams(pageCount)}>{pageCount}</button>
          </>
        ) : null}
        <button type="button" className="cpDsPagerBtn" disabled={currentPage >= pageCount} onClick={() => this.setCurrentPageParams(currentPage + 1)}>Next</button>
      </div>
    );
  };

  renderClubProgrammesDsPage = (): React.ReactNode => {
    const selectedProgramme = this.state.previewItem;
    const classes = 'cpDsPage';
    const activeFilterChips = this.getActiveFilterChips();
    const visibleColumns = this.getVisibleProgrammeColumns();

    return (
      <div className={classes}>
        <div className="cpDsMain">
          <header className="cpDsHeader">
            <div className="cpDsHeaderTitle">
              <h1>Activity programmes</h1>
              <p>
                {this.state.totalWithFilters}{' '}
                {this.state.totalWithFilters === 1 ? 'programme' : 'programmes'}
                {this.state.isDataFiltered ? ` of ${this.state.total}` : ''}
              </p>
            </div>
            <div className="cpDsToolbar">
              <label className="cpDsSearch">
                <span>Programme</span>
                <input
                  value={String(this.state.filters?.name || '')}
                  onChange={event => this.onTableFilterChange(event, 'name')}
                  onKeyDown={event => {
                    if (event.key === 'Enter') this.onApplyFilterClick();
                  }}
                  placeholder="Search programmes…"
                />
              </label>
              <div className="cpDsFilterControl">
                {this.state.isShowFilter ? (
                  <button
                    type="button"
                    className="cpDsFilterScrim"
                    aria-label="Dismiss filters"
                    onClick={this.onCancelFilterClick}
                  />
                ) : null}
                <button
                  type="button"
                  className={`cpDsIconTextBtn ${this.state.isShowFilter ? 'isActive' : ''}`}
                  onClick={this.onTableFilterClick}
                  aria-expanded={this.state.isShowFilter}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M4 6h16M7 12h10M10 18h4" />
                  </svg>
                  Filters
                  {activeFilterChips.length ? <span className="cpDsFilterCount">{activeFilterChips.length}</span> : null}
                </button>
                {this.state.isShowFilter ? (
                  <div
                    className="cpDsFilterDrawer sit-drawer sit-drawer--right"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Programme filters"
                    onKeyDown={event => {
                      const target = event.target as HTMLElement;
                      if (event.key === 'Enter' && target.tagName === 'INPUT') {
                        event.preventDefault();
                        this.onApplyFilterClick();
                      }
                    }}
                  >
                    <header>
                      <strong>Filters</strong>
                      <button type="button" onClick={this.onCancelFilterClick} aria-label="Close filters">
                        ×
                      </button>
                    </header>
                    {this.renderDsFilters()}
                    <footer>
                      <button type="button" className="isClear" onClick={this.onClearFilterClick}>
                        Clear all
                      </button>
                      <div>
                        <button type="button" onClick={this.onCancelFilterClick}>
                          Cancel
                        </button>
                        <button type="button" className="isApply" onClick={this.onApplyFilterClick}>
                          Apply
                        </button>
                      </div>
                    </footer>
                  </div>
                ) : null}
              </div>
              <div className="cpDsColumnsControl">
                {this.state.isColumnsOpen ? (
                  <button
                    type="button"
                    className="cpDsColumnsScrim"
                    aria-label="Close column settings"
                    onClick={() => this.setState({ isColumnsOpen: false })}
                  />
                ) : null}
                <button
                  type="button"
                  className={`cpDsIconTextBtn ${this.state.isColumnsOpen ? 'isActive' : ''}`}
                  onClick={() =>
                    this.setState(prev => ({
                      isColumnsOpen: !prev.isColumnsOpen,
                      isShowFilter: false
                    }))
                  }
                  aria-expanded={this.state.isColumnsOpen}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <rect x="4" y="5" width="16" height="14" rx="2" />
                    <path d="M10 5v14M15 5v14" />
                  </svg>
                  Columns <span className="cpDsButtonBadge">{visibleColumns.length}</span>
                </button>
                {this.state.isColumnsOpen ? (
                  <div className="cpDsColumnsMenu">
                    <header>
                      <span>Show columns</span>
                      <button type="button" onClick={this.resetColumns}>Reset</button>
                    </header>
                    {CLUB_PROGRAMMES_GRID_COLUMNS.map(column => (
                      <label key={column.field}>
                        <input
                          type="checkbox"
                          checked={column.field === 'name' || this.state.visibleColumns[column.field]}
                          disabled={column.field === 'name'}
                          onChange={() => this.toggleColumn(column.field)}
                        />
                        <span>{column.field === 'name' ? 'Programme' : column.text}</span>
                      </label>
                    ))}
                  </div>
                ) : null}
              </div>
              <button type="button" className="cpDsBtn cpDsBtnPrimary" onClick={this.openCreateModal}>
                <span aria-hidden="true">＋</span> Create programme
              </button>
            </div>
          </header>

          <section className="cpDsContentCard">
            {activeFilterChips.length ? (
              <div className="cpDsActiveFilters">
                {activeFilterChips.map(chip => (
                  <button type="button" key={chip.field} onClick={() => this.removeAppliedFilter(chip.field)}>
                    <span>{chip.label}</span>
                    <span aria-hidden="true">×</span>
                  </button>
                ))}
                <button type="button" className="cpDsClearFilters" onClick={this.onClearFilterClick}>
                  Clear all
                </button>
              </div>
            ) : null}
            {this.renderDsSelectionBar()}
            {this.renderDsTable()}
            {this.renderDsPagination()}
          </section>
        </div>

        {selectedProgramme ? (
          <>
            <div className="cpDsSummaryScrim" onClick={this.closeProgrammePreview} />
            <aside className="cpDsSummary sit-drawer sit-drawer--right">
              <ClubProgrammesSummaryPanel
                user={this.props.user}
                selectedProgramme={selectedProgramme}
                clubDetailsById={this.state.clubDetailsById}
                currentTabIndex={this.state.currentTabIndex}
                onTabClick={this.onTabClick}
                onProgrammeUpdated={this.onProgrammeUpdated}
                onOpenManageClubsPage={programme => {
                  const programmeId = String((programme as any)?.id || (programme as any)?._id || '');
                  this.props.history.push({
                    pathname: `/clubProgrammes/${programmeId}/manageclubs`,
                    state: {
                      from: {
                        pathname: this.props.location.pathname,
                        search: this.getBackSearch(programmeId)
                      }
                    }
                  });
                }}
                onOpenBookingsManager={programme => {
                  const programmeId = String((programme as any)?.id || (programme as any)?._id || '');
                  this.props.history.push({
                    pathname: `/clubProgrammes/${programmeId}/bookings`,
                    state: {
                      from: {
                        pathname: this.props.location.pathname,
                        search: this.getBackSearch(programmeId)
                      }
                    }
                  });
                }}
                onClose={this.closeProgrammePreview}
              />
            </aside>
          </>
        ) : null}
      </div>
    );
  };
  render() {
    if (this.state.isLoading) {
      return <Loader />;
    }

    const classes =
      this.state.isDeleteModalOpen ||
      this.state.isErrorModalOpen ||
      this.state.isActivationWarningModalOpen ||
      this.state.isStatusModalOpen ||
      this.state.isHelpOpen
        ? 'clubProgrammesRoute modal-open'
        : 'clubProgrammesRoute';

    return (
      <div className={classes}>
        <ClubProgrammeDeleteModal
          isOpen={this.state.isDeleteModalOpen}
          selectedItems={this.state.selectedItems}
          onDelete={this.onDeleteProgrammeClick}
          onClose={this.closeDeleteModal}
        />
        <ClubProgrammeActivationWarningModal
          isOpen={this.state.isActivationWarningModalOpen}
          items={this.state.activationWarningItems}
          onConfirm={this.runActivateProgrammes}
          onClose={() =>
            this.setState({
              isActivationWarningModalOpen: false,
              activationWarningItems: []
            })
          }
        />
        <ClubProgrammeErrorModal
          isOpen={this.state.isErrorModalOpen}
          errorMessage={this.state.errorMessage}
          onClose={() => this.setState({ isErrorModalOpen: false, errorMessage: '' })}
        />

        <ClubProgrammeStatusModal
          isOpen={this.state.isStatusModalOpen}
          status={this.state.statusModalType}
          title={this.state.statusModalTitle}
          message={this.state.statusModalMessage}
          lines={this.state.statusModalLines}
          onClose={() =>
            this.setState({
              isStatusModalOpen: false,
              statusModalType: 'success',
              statusModalTitle: '',
              statusModalMessage: '',
              statusModalLines: []
            })
          }
        />

        <HelpModal
          isOpen={this.state.isHelpOpen}
          title="How activity programmes work"
          badgeText="Activity Programmes"
          subtitle="Use this area to create booking programmes, prepare them in draft, attach clubs, control booking visibility and reminders, review bookings and move the programme through to activation."
          onClose={() => this.setState({ isHelpOpen: false })}
        >
          <div className="row">
            <div className="col-md-6 mb-3">
              <div className="border rounded p-3 h-100">
                <div className="font-weight-bold mb-2">What a programme is</div>
                <div className="text-muted">
                  A programme is a booking container for a group of clubs. It defines the booking window, audience,
                  selection mode, visibility behaviour and reminder audience before clubs are managed inside it.
                </div>
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <div className="border rounded p-3 h-100">
                <div className="font-weight-bold mb-2">Main workflow</div>
                <ol className="mb-0 pl-3 text-muted">
                  <li>Create a programme.</li>
                  <li>Set booking window, audience, visibility and selection rules.</li>
                  <li>Add eligible clubs to the programme.</li>
                  <li>Manage programme fields and club setup.</li>
                  <li>After booking closes, review and confirm allocations.</li>
                  <li>Activate clubs when allocation work is complete.</li>
                </ol>
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <div className="border rounded p-3 h-100">
                <div className="font-weight-bold mb-2">Statuses</div>
                <div className="mb-2">
                  <span className="badge badge-secondary mr-2">DRAFT</span>
                  Programme is being prepared and can still be edited.
                </div>
                <div className="mb-2">
                  <span className="badge badge-success mr-2">ACTIVE</span>
                  Programme is live and used for booking and follow-up workflow.
                </div>
                <div>
                  <span className="badge badge-dark mr-2">ARCHIVED</span>
                  Programme is no longer active, but kept for history.
                </div>
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <div className="border rounded p-3 h-100">
                <div className="font-weight-bold mb-2">Selection modes</div>
                <div className="mb-2">
                  <span className="badge badge-light border mr-2">SINGLE</span>
                  Booker chooses one club per day.
                </div>
                <div className="mb-2">
                  <span className="badge badge-light border mr-2">PRIORITY</span>
                  Booker ranks preferences for each day.
                </div>
                <div className="text-muted">
                  Once a programme is active, the selection mode and priority range can no longer be changed.
                </div>
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <div className="border rounded p-3 h-100">
                <div className="font-weight-bold mb-2">Visibility modes</div>
                <div className="mb-2">
                  <span className="badge badge-light border mr-2">HIDE</span>
                  Before booking opens, parents/students cannot see the programme.
                </div>
                <div className="mb-2">
                  <span className="badge badge-light border mr-2">VIEW</span>
                  Before booking opens, they can see the programme, but cannot submit selections yet.
                </div>
                <div>
                  <span className="badge badge-light border mr-2">DRAFT</span>
                  Internal draft visibility mode for staff preparation rather than normal public booking access.
                </div>
                <div className="text-muted mt-2">
                  Once a programme is active, this setting can no longer be changed.
                </div>
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <div className="border rounded p-3 h-100">
                <div className="font-weight-bold mb-2">Submitted booking changes</div>
                <div className="text-muted mb-2">
                  This setting controls whether parents or students can change their choices after they have submitted
                  them.
                </div>
                <div className="mb-2">
                  <span className="badge badge-light border mr-2">Enabled</span>
                  Bookers can still edit or remove submitted selections until booking closes.
                </div>
                <div className="mb-2">
                  <span className="badge badge-light border mr-2">Disabled</span>
                  Submitted choices become locked after submission.
                </div>
                <div className="text-muted">This setting can only be changed before the programme is activated.</div>
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <div className="border rounded p-3 h-100">
                <div className="font-weight-bold mb-2">Reminder notifications</div>
                <div className="text-muted mb-2">
                  Reminder notifications depend on both programme setup and system notification settings.
                </div>
                <ul className="mb-0 pl-3 text-muted">
                  <li>Programme audience filters define which ages / genders are in scope.</li>
                  <li>
                    <b>Send reminders to</b> controls whether reminders go to parents, students or both.
                  </li>
                  <li>
                    Matching system notifications can be sent for booking opening soon, booking opened and booking
                    closing soon.
                  </li>
                  <li>
                    Once reminder timing becomes configurable here, this help can also show the exact intervals used.
                  </li>
                  <li>Audience settings define which ages, genders and recipient roles are included.</li>
                  <li>Once a programme is active, audience settings can no longer be changed.</li>
                </ul>
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <div className="border rounded p-3 h-100">
                <div className="font-weight-bold mb-2">Lifecycle and activation</div>
                <div className="text-muted mb-2">
                  The programme behaves differently before, during and after the booking window.
                </div>
                <ul className="mb-0 pl-3 text-muted">
                  <li>
                    Before booking opens, users may either not see the programme, or see it without being able to
                    submit, depending on visibility mode.
                  </li>
                  <li>After booking opens, eligible parents/students can submit their selections.</li>
                  <li>After booking closes, staff review and process those selections in the Bookings tab.</li>
                  <li>
                    Once allocations are complete, activating programme clubs creates the live event chains from the
                    confirmed outcomes.
                  </li>
                  <li>
                    After programme clubs are activated and the programme becomes <b>completed</b>, parents and students
                    will no longer see that programme in their booking lists, even if the original booking close date
                    has not yet been reached.
                  </li>
                </ul>
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <div className="border rounded p-3 h-100">
                <div className="font-weight-bold mb-2">What you can do from this page</div>
                <ul className="mb-0 pl-3 text-muted">
                  <li>Create, edit, activate and delete programmes.</li>
                  <li>Filter and sort the programme list.</li>
                  <li>Open the Clubs tab to add or manage linked clubs.</li>
                  <li>Open the Fields tab to manage programme fields.</li>
                  <li>Open the Bookings tab to go into allocation management after booking closes.</li>
                </ul>
              </div>
            </div>

            <div className="col-md-6 mb-3">
              <div className="border rounded p-3 h-100">
                <div className="font-weight-bold mb-2">Important rules</div>
                <ul className="mb-0 pl-3 text-muted">
                  <li>Only draft programmes can be freely edited.</li>
                  <li>Delete is only available when a draft programme has no clubs inside it.</li>
                  <li>Activation is intended for programmes that are fully prepared and allocated.</li>
                  <li>The summary panel on the right gives access to clubs, fields and bookings tools.</li>
                  <li>Selection mode and priority range cannot be changed once a programme is active.</li>
                  <li>Visibility and reminder audience settings cannot be changed once a programme is active.</li>
                  <li>The submitted booking changes setting can only be changed before programme activation.</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="border rounded p-3 mt-2" style={{ background: '#fffdf3' }}>
            <div className="font-weight-bold mb-2">Tip</div>
            <div className="text-muted mb-0">
              A good working order is: create the programme, confirm visibility, reminder audience and booking window,
              add clubs, review the summary tabs, let booking run, process allocations after booking closes, and only
              then activate clubs.
            </div>
          </div>
        </HelpModal>

        {this.renderClubProgrammesDsPage()}
      </div>
    );
  }
}
